import { createContext, useContext, useEffect, useState, useCallback, type ReactNode } from 'react';
import { creditService, type CreditSummary } from '../services/creditService';

export const LOW_CREDIT_THRESHOLD = 50;

interface CreditContextValue {
    credits: number;
    loading: boolean;
    summary: CreditSummary | null;
    showPaywall: boolean;
    openPaywall: () => void;
    closePaywall: () => void;
    refresh: (force?: boolean) => void;
}

const CreditContext = createContext<CreditContextValue>({
    credits: 0,
    loading: true,
    summary: null,
    showPaywall: false,
    openPaywall: () => {},
    closePaywall: () => {},
    refresh: () => {},
});

export function CreditProvider({ children }: { children: ReactNode }) {
    const [summary, setSummary]         = useState<CreditSummary | null>(null);
    const [loading, setLoading]         = useState(true);
    const [showPaywall, setShowPaywall] = useState(false);

    const refresh = useCallback(async (force = false) => {
        try {
            const data = await creditService.getSummary(force);
            setSummary(data);
        } catch { /* silent */ } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        refresh();
        // Subscribe to credit service updates (triggered after charges)
        const unsub = creditService.onUpdate(data => {
            setSummary(data);
            setLoading(false);
        });
        return unsub;
    }, [refresh]);

    return (
        <CreditContext.Provider value={{
            credits:     summary?.available_credits ?? 0,
            loading,
            summary,
            showPaywall,
            openPaywall:  () => setShowPaywall(true),
            closePaywall: () => setShowPaywall(false),
            refresh,
        }}>
            {children}
        </CreditContext.Provider>
    );
}

export function useCredits() {
    return useContext(CreditContext);
}
